import React, { useState } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface TipSelectionProps {
  subtotal: number;
  onTipChange: (tipAmount: number) => void;
}

const TipSelection: React.FC<TipSelectionProps> = ({ subtotal, onTipChange }) => {
  const [tipType, setTipType] = useState<'percentage' | 'custom'>('percentage');
  const [tipPercentage, setTipPercentage] = useState<number | null>(null);
  const [customTipAmount, setCustomTipAmount] = useState<string>('');
  
  // Common tip percentages with a note about average tips
  const tipOptions = [
    { value: 15, label: '15%', tooltip: 'Standard tip' },
    { value: 20, label: '20%', tooltip: 'Good service tip' },
    { value: 25, label: '25%', tooltip: 'Great service tip' },
    { value: 30, label: '30%', tooltip: 'Exceptional service tip' }
  ];

  // Calculate tip amount based on percentage
  const calculateTipFromPercentage = (percentage: number): number => {
    return (subtotal * percentage) / 100;
  };

  // Handle percentage tip selection
  const handleTipPercentageChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPercentage: number | null
  ) => {
    setTipType('percentage');
    setTipPercentage(newPercentage);
    setCustomTipAmount('');
    
    if (newPercentage !== null) {
      const tipAmount = calculateTipFromPercentage(newPercentage);
      onTipChange(tipAmount);
    } else {
      onTipChange(0);
    }
  };

  // Handle custom tip input
  const handleCustomTipChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    
    // Allow only numbers and decimal point
    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
      setTipType('custom');
      setTipPercentage(null);
      setCustomTipAmount(value);
      
      const tipAmount = parseFloat(value) || 0;
      onTipChange(tipAmount);
    }
  };

  // Calculate and format the tip amount to display
  const getTipAmount = (): number => {
    if (tipType === 'percentage' && tipPercentage !== null) {
      return calculateTipFromPercentage(tipPercentage);
    } else if (tipType === 'custom' && customTipAmount) {
      return parseFloat(customTipAmount) || 0;
    }
    return 0;
  };

  const tipAmount = getTipAmount();
  const tipPercentageText = tipType === 'percentage' && tipPercentage !== null 
    ? `(${tipPercentage}%)` 
    : '';

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="h2">
          Add a Tip
        </Typography>
        <Tooltip title="Tips are appreciated by our staff and are a great way to show appreciation for excellent service">
          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
        </Tooltip>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Our most common tip is 20%. All tips go directly to your service provider.
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          exclusive
          value={tipPercentage}
          onChange={handleTipPercentageChange}
          aria-label="tip percentage"
          fullWidth
          sx={{ mb: 2 }}
        >
          {tipOptions.map((option) => (
            <ToggleButton 
              key={option.value} 
              value={option.value}
              aria-label={`${option.value}% tip`}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    color: 'white',
                  }
                }
              }}
            >
              <Tooltip title={option.tooltip}>
                <Typography>{option.label}</Typography>
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        
        <TextField
          label="Custom Tip"
          variant="outlined"
          fullWidth
          value={customTipAmount}
          onChange={handleCustomTipChange}
          onClick={() => setTipType('custom')}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          placeholder="Enter custom amount"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: tipType === 'custom' ? 'primary.main' : undefined,
                  borderWidth: tipType === 'custom' ? 2 : undefined,
                },
              },
            },
          }}
        />
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body1">Tip Amount {tipPercentageText}</Typography>
        <Typography variant="body1">${tipAmount.toFixed(2)}</Typography>
      </Box>
    </Paper>
  );
};

export default TipSelection;
